import type { FtuStepMachine } from '../hooks/useFtuStepMachine'
import { useLightMode } from '@/hooks/useLightMode'
import { Trans, useTranslation } from 'react-i18next'

import { Box, Flex, HStack, Kbd, Text } from '@chakra-ui/react'

import { DarkProvider, Modal } from '@hivewatch/dark'

import { Button } from '@/components/ui'
import { tVar } from '@/utils/i18n'

import { FTU_MODAL_Z_INDEX } from '../constants/constants'

interface FtuStepCardIProps {
  machine: FtuStepMachine
}

/**
 * Renders the current FTU step using the shared rebrand modal (`@hivewatch/dark`
 * `Modal`, wrapped in `DarkProvider`).
 *
 * The tour is gated: `onClose` is a no-op and the modal has no close button, so
 * the overlay/Escape can't dismiss it — it closes only on completion, when
 * `FtuManager` stops rendering it. The primary button carries the step action
 * (Next / Finish / Got it); Back and the "x of n" counter sit in the footer.
 */
export const FtuStepCard = ({ machine }: FtuStepCardIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const {
    currentStep,
    stepIndex,
    stepCount,
    isFirstStep,
    isLastStep,
    canProceed,
    isActionImpossible,
    next,
    back,
    finish,
  } = machine

  const BodyComponent = currentStep.bodyComponent

  const getPrimaryLabel = () => {
    if (isLastStep) {
      return t('first_time_use:finish')
    } else if (isActionImpossible) {
      return t('first_time_use:got_it')
    } else {
      return t('first_time_use:next')
    }
  }

  const footer = (
    <Flex alignItems='center' justifyContent='space-between' w='100%'>
      {/* A "1 of 1" counter is noise on a single-step tour. The empty Text
          keeps the buttons right-aligned under space-between. */}
      <Text color='gray.500' fontSize='sm'>
        {stepCount > 1 &&
          tVar(t, 'first_time_use:step_counter', {
            current: stepIndex + 1,
            total: stepCount,
          })}
      </Text>
      <HStack spacing={3}>
        {!isFirstStep && (
          <Button onClick={back} rounded='0px' variant='v2-ghost'>
            {t('first_time_use:back')}
          </Button>
        )}
        <Button
          data-testid='ftu_primary_button'
          isDisabled={!canProceed}
          onClick={isLastStep ? finish : next}
          rounded='0px'
          variant={`v2-primary-${mode}`}
        >
          {getPrimaryLabel()}
        </Button>
      </HStack>
    </Flex>
  )

  return (
    <DarkProvider>
      <Modal
        colorScheme={mode}
        footer={footer}
        footerStyleOverrides={{ width: '100%' }}
        isCloseHidden
        isOpen
        isSaveHidden
        onClose={() => undefined}
        size='compact'
        title={tVar(t, currentStep.titleKey, {})}
        zIndex={FTU_MODAL_Z_INDEX}
      >
        <Text data-testid='ftu_step_card'>
          <Trans
            components={{
              kbd: <Kbd bg={`text.primary.${mode}`} color={`bg.app.${mode}`} />,
            }}
            i18nKey={currentStep.bodyKey as never}
          />
        </Text>
        {BodyComponent && (
          // The gap below the body content is fixed by the modal (24px body
          // bottom padding + 30px footer top padding); match it above so the
          // embedded content sits evenly between the text and the footer.
          <Box mt='54px'>
            <BodyComponent />
          </Box>
        )}
      </Modal>
    </DarkProvider>
  )
}
